import { Feature, MultiPolygon, Polygon } from "geojson";
import {
    getIrrigationSystemSpeed,
    IrrigationSystem,
} from "@/types/irrigationSystem";
import { IrrigationSystemSettings } from "@/types/rx";
import { polygonsOverlap } from "./map";

export type IrrDetailsCalcParams = {
    pivotSpeedPercent: string | number;
    irrigationDepth: string | number | undefined;
    pivotMinDepth: number;
    precision?: number;
};

export const calculateIrrigationDetails = (
    params: IrrDetailsCalcParams,
): IrrDetailsCalcParams => {
    const { pivotSpeedPercent, pivotMinDepth, irrigationDepth, precision } =
        params;

    const speed: number = +pivotSpeedPercent;
    const irrDepth: number = +(irrigationDepth ?? 0);
    const minDepth: number = pivotMinDepth;
    const numsAfterDecimal: number =
        precision && precision >= 0 ? precision : 2;

    if (irrDepth > 0) {
        params.pivotSpeedPercent = ((minDepth / irrDepth) * 100).toFixed(
            numsAfterDecimal,
        );
        params.irrigationDepth = irrDepth.toFixed(numsAfterDecimal);
    } else if (speed > 0) {
        params.pivotSpeedPercent = speed.toFixed(numsAfterDecimal);
        params.irrigationDepth = (minDepth / (speed / 100)).toFixed(
            numsAfterDecimal,
        );
    } else {
        const emptyNum = 0;
        params.pivotSpeedPercent = emptyNum.toFixed(numsAfterDecimal);
        params.irrigationDepth = emptyNum.toFixed(numsAfterDecimal);
    }
    return params;
};

export function normalizeIrrigationSystemIds(value: unknown): number[] {
    if (Array.isArray(value)) {
        return value
            .map((item) => {
                if (typeof item === "number" && Number.isFinite(item)) {
                    return item;
                }
                if (typeof item === "string" && item !== "") {
                    const parsed = Number(item);
                    return Number.isFinite(parsed) ? parsed : null;
                }
                if (
                    item &&
                    typeof item === "object" &&
                    "id" in item &&
                    Number.isFinite(Number((item as { id: unknown }).id))
                ) {
                    return Number((item as { id: unknown }).id);
                }
                return null;
            })
            .filter((id): id is number => id != null);
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return [value];
    }
    if (typeof value === "string" && value !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? [parsed] : [];
    }
    return [];
}

export function getFeatureIrrigationSystemId(feature: {
    irrigation_system?: unknown;
    properties?: {
        irrigation_system?: unknown;
        irrigation_system_id?: unknown;
    } | null;
}): number | null {
    const raw =
        feature.irrigation_system ??
        feature.properties?.irrigation_system ??
        feature.properties?.irrigation_system_id;
    if (raw == null || raw === "") return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
}

export function irrigationSystemCoversFeature(
    system: IrrigationSystem,
    feature: Feature,
): boolean {
    if (!feature.geometry || !system.boundary) return false;
    try {
        return polygonsOverlap(feature as Feature<Polygon | MultiPolygon>, {
            type: "Feature",
            geometry: system.boundary,
            properties: {},
        });
    } catch {
        return false;
    }
}

export function resolveFeatureIrrigationSystemId(
    feature: Feature,
    systems: IrrigationSystem[],
): number | null {
    const taggedId = getFeatureIrrigationSystemId(feature);
    if (taggedId != null) return taggedId;
    const match = systems.find((system) =>
        irrigationSystemCoversFeature(system, feature),
    );
    return match?.id ?? null;
}

export function featureMatchesIrrigationSystem(
    feature: Feature,
    systemId: number,
    system?: IrrigationSystem,
): boolean {
    const taggedId = getFeatureIrrigationSystemId(feature);
    if (taggedId != null) return taggedId === systemId;
    if (system) return irrigationSystemCoversFeature(system, feature);
    return false;
}

export function indexIrrigationSystemSettings(
    settings?: IrrigationSystemSettings[] | null,
): Record<number, IrrigationSystemSettings> {
    return Object.fromEntries(
        (settings ?? []).map((row) => [Number(row.irrigation_system), row]),
    );
}

export function knobsFromIrrigationSystemSettings(
    settings?: IrrigationSystemSettings | null,
    fallback?: Partial<IrrigationSystemSettings> | null,
    system?: IrrigationSystem | null,
): Required<
    Pick<
        IrrigationSystemSettings,
        | "depth_inches"
        | "speed_percentage"
        | "injection_rate_gal_per_hour"
        | "max_speed_percentage"
    >
> {
    const depth = settings?.depth_inches ?? fallback?.depth_inches ?? 0.5;
    const speed =
        settings?.speed_percentage ??
        fallback?.speed_percentage ??
        (system != null ? getIrrigationSystemSpeed(system, depth) : null);
    return {
        depth_inches: depth,
        speed_percentage: speed,
        injection_rate_gal_per_hour:
            settings?.injection_rate_gal_per_hour ??
            fallback?.injection_rate_gal_per_hour ??
            null,
        max_speed_percentage:
            settings?.max_speed_percentage ??
            fallback?.max_speed_percentage ??
            100,
    };
}

export function buildIrrigationSystemSettings(
    systemIds: number[],
    cache: Record<number, IrrigationSystemSettings>,
    active?: IrrigationSystemSettings | null,
    options?: {
        defaultDepth?: number | null;
        defaultMaxSpeed?: number | null;
        systems?: IrrigationSystem[];
    },
): IrrigationSystemSettings[] {
    const merged = { ...cache };
    if (active?.irrigation_system != null) {
        merged[Number(active.irrigation_system)] = active;
    }
    const defaultDepth = options?.defaultDepth ?? 0.5;
    const defaultMaxSpeed = options?.defaultMaxSpeed ?? 100;
    return systemIds.map((id) => {
        const stored = merged[id];
        if (stored) {
            return {
                irrigation_system: id,
                depth_inches: stored.depth_inches ?? defaultDepth,
                speed_percentage: stored.speed_percentage ?? null,
                injection_rate_gal_per_hour:
                    stored.injection_rate_gal_per_hour ?? null,
                max_speed_percentage:
                    stored.max_speed_percentage ?? defaultMaxSpeed,
            };
        }
        const system = options?.systems?.find((item) => item.id === id);
        return {
            irrigation_system: id,
            depth_inches: defaultDepth,
            speed_percentage:
                system != null
                    ? getIrrigationSystemSpeed(system, defaultDepth)
                    : null,
            injection_rate_gal_per_hour: null,
            max_speed_percentage: defaultMaxSpeed,
        };
    });
}
